import React, { useState, useEffect } from 'react';
import {
    Divider,
    Grid,
    Typography,
    IconButton,
    Dialog,
    DialogContent,
    DialogTitle,
    Tooltip,
    Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    CancelOutlined,
    CheckCircleOutline,
    ImageSearchRounded
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { classificationRegressionTextColors } from '../../utils/useTextColors';

type AccuracyPoint = {
    trial: number;
    value: number;
};

type TrainingDetailsProps = {
    trainingDetails: any;
    accuracyPlotDataJson?: AccuracyPoint[];
};

const TrainingDetails: React.FC<TrainingDetailsProps> = ({ trainingDetails, accuracyPlotDataJson }) => {
    const { t } = useTranslation();
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    const handleOpenDialog = () => setOpenImageDialog(true);
    const handleCloseDialog = () => setOpenImageDialog(false);
    const handleMaximize = () => setIsMaximized(true);
    const handleMinimize = () => setIsMaximized(false);
    const [chartDimensions, setChartDimensions] = useState({ width: 800, height: 400 });

    useEffect(() => {
        const updateDimensions = () => {
            if (isMaximized) {
                const padding = 100;
                const maxWidth = window.innerWidth - padding;
                const maxHeight = window.innerHeight - padding - 100;
                setChartDimensions({
                    width: maxWidth,
                    height: maxHeight,
                });
            } else {
                setChartDimensions({ width: 800, height: 400 });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [isMaximized]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <Grid mt={1.5} container display="flex" justifyContent="space-between">
                    <Grid item fontWeight="bold">{t('SUCCESSFUL_TRAINING_SUCCESSFULL_TRAINING')}</Grid>
                    <Grid item>
                        {trainingDetails?.success
                            ? <CheckCircleOutline sx={{ color: "green" }} />
                            : <CancelOutlined sx={{ color: 'red' }} />}
                    </Grid>
                </Grid>
            </Grid>

            <Grid mb={1.5} mt={0.5} item xs={12}><Divider /></Grid>

            <Grid item xs={12} fontWeight="bold">
                {t('SUCCESSFUL_TRAINING_FEATURE_SIGNIFICANCE')}
            </Grid>

            <Grid item xs={12} sx={{ height: '200px', overflowY: 'auto' }}>
                {trainingDetails?.featureSignificance?.slice()?.sort(
                    (a: { name: string, significance: number }, b: { name: string, significance: number }) =>
                        Math.abs(b.significance) - Math.abs(a.significance)
                )?.map((el: { name: string, significance: number }, index: number) => (
                    <Grid key={index} item xs={12} pr={1}>
                        <Grid container display="flex" justifyContent="space-between">
                            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography>{index + 1}.&nbsp;{el.name}</Typography>
                            </Grid>
                            <Grid item>{el.significance}</Grid>
                        </Grid>
                    </Grid>
                ))}
            </Grid>

            <Grid mt={1.5} mb={2} item xs={12}><Divider /></Grid>

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item fontWeight="bold">{t('SUCCESSFUL_TRAINING_SELECTED_MODEL_TYPE')}</Grid>
                    <Grid item>
                        {
                            trainingDetails?.selectedModelTypeString ? 
                            (
                                <Chip
                                    label={t(trainingDetails.selectedModelTypeString)}
                                    size="small"
                                    sx={{
                                        color: classificationRegressionTextColors[trainingDetails.selectedModelTypeString]?.color,
                                        backgroundColor: classificationRegressionTextColors[trainingDetails.selectedModelTypeString]?.backgroundColor,
                                        fontWeight: 'medium',
                                    }}
                                />
                            ) : 
                            (
                                '---'
                            )
                        }
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item fontWeight="bold">{t('SUCCESSFUL_TRAINING_SELECTED_MODEL_SUBTYPE')}</Grid>
                    <Grid item>{trainingDetails?.selectedModelSubtype ? t(`${trainingDetails?.selectedModelSubtype}`) : '---'}</Grid>
                </Grid>
            </Grid>

            {trainingDetails?.showAccuracy && (
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between">
                        <Typography sx={{ fontWeight: 'bold' }}>
                            {t('SUCCESSFUL_TRAINING_ACCURACY')}
                        </Typography>
                        <Grid item>
                            {trainingDetails?.accuracy != null ? (
                                <Grid container alignItems="center" spacing={1}>
                                    {accuracyPlotDataJson && (
                                        <Grid item>
                                            <IconButton
                                                onClick={handleOpenDialog}
                                                size="small"
                                                className='image-icon'
                                                sx={{ padding: 0 }}
                                            >
                                                <ImageSearchRounded />
                                            </IconButton>
                                        </Grid>
                                    )}
                                    <Grid item>
                                        {(trainingDetails.accuracy * 100).toFixed(2)}%
                                    </Grid>
                                </Grid>
                            ) : '---'}
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {trainingDetails?.showRmse && (
                <Grid item xs={12}>
                    <Grid container display="flex" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 'bold' }}>
                            {t('SUCCESSFUL_TRAINING_ACCURACY')}
                        </Typography>
                        <Grid item>
                            {trainingDetails?.rmse != null ? (
                                <Grid container alignItems="center" spacing={1}>
                                    {accuracyPlotDataJson && (
                                        <Grid item>
                                            <IconButton
                                                onClick={handleOpenDialog}
                                                size="small"
                                                className='image-icon'
                                                sx={{ padding: 0 }}
                                            >
                                                <ImageSearchRounded />
                                            </IconButton>
                                        </Grid>
                                    )}
                                    <Grid item>
                                        {trainingDetails?.rmse.toFixed(2)}
                                    </Grid>
                                </Grid>
                            ) : '---'}
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {trainingDetails?.showPrecisionTestDataSet && (
                <Grid item xs={12}>
                    <Grid container display="flex" justifyContent="space-between">
                        <Grid item>{t('SUCCESSFUL_TRAINING_TEST_DATASET_PRECISION')}</Grid>
                        <Grid item>{trainingDetails?.precisionTestDataSet ? (trainingDetails?.precisionTestDataSet * 100.0).toFixed(2) + "%" : '---'}</Grid>
                    </Grid>
                </Grid>
            )}

            {trainingDetails?.showPrecisionTrainDataSet && (
                <Grid item xs={12}>
                    <Grid container display="flex" justifyContent="space-between">
                        <Grid item>{t('SUCCESSFUL_TRAINING_TRAIN_DATASET_PRECISION')}</Grid>
                        <Grid item>{trainingDetails?.precisionTrainDataSet ? (trainingDetails?.precisionTrainDataSet * 100.0).toFixed(2) + "%" : '---'}</Grid>
                    </Grid>
                </Grid>
            )}

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item>{t('SUCCESSFUL_TRAINING_TEST_DATASET_SIZE')}</Grid>
                    <Grid item>{trainingDetails?.sizeTestDataSet ?? '---'}</Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item>{t('SUCCESSFUL_TRAINING_TRAIN_DATASET_SIZE')}</Grid>
                    <Grid item>{trainingDetails?.sizeTrainDataSet ?? '---'}</Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item>{t('SUCCESSFUL_TRAINING_TRAIN_DATE')}</Grid>
                    <Grid item>{trainingDetails?.startTrainDateTimeUTC ?? '---'}</Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container display="flex" justifyContent="space-between">
                    <Grid item>{t('SUCCESSFUL_TRAINING_TRAIN_DURATION')}</Grid>
                    <Grid item>{trainingDetails?.trainDuration ?? '---'}</Grid>
                </Grid>
            </Grid>

            <Dialog 
                open={openImageDialog} 
                onClose={handleCloseDialog} 
                maxWidth={isMaximized ? false : 'md'} 
                fullWidth={!isMaximized}
                sx={isMaximized ? { '& .MuiDialog-paper': { width: '100%', height: '100%', maxHeight: '100%' } } : {}}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('ACCURACY_PLOT_TITLE')}
                    <Grid>
                        {isMaximized ? (
                            <Tooltip title={t("MINIMIZE")}>
                                <IconButton
                                    onClick={handleMinimize}
                                    aria-label="minimize"
                                    sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
                                >
                                    <FullscreenExitIcon />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={t("MAXIMIZE")}>
                                <IconButton
                                    onClick={handleMaximize}
                                    aria-label="maximize"
                                    sx={{
                                        color: '#1976d2',
                                        '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            transform: 'scale(1.2)',
                                            transition: 'transform 0.2s ease-in-out',
                                        },
                                    }}
                                >
                                    <FullscreenIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Close">
                            <IconButton
                                onClick={handleCloseDialog}
                                aria-label="close"
                                sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </DialogTitle>
                <DialogContent sx={{display:'flex', justifyContent:"center"}}>
                    {accuracyPlotDataJson &&
                    <Grid container sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <LineChart
                            xAxis={[
                                {
                                    data: accuracyPlotDataJson.map(({ trial }) => trial),
                                    label: t('ACCURACY_PLOT_X_LABEL'),
                                    labelStyle: {
                                        transform: 'translateY(10px)',
                                    },
                                },
                            ]}
                            yAxis={[
                                {
                                    label: trainingDetails?.showRmse ? t('ACCURACY_PLOT_Y_RMSE_LABEL') : t('ACCURACY_PLOT_Y_PRECISION_LABEL'),
                                    labelStyle: {
                                        transform: 'translateX(-40px)'
                                    },
                                },
                            ]}
                            series={[
                                {
                                    data: accuracyPlotDataJson.map(({ value }) => value),
                                    showMark: (params) => true,
                                },
                            ]}
                            margin={{ top: 50, bottom: 50, left: 100, right: 30 }}
                            height={chartDimensions.height}
                            width={chartDimensions.width}
                        />
                    </Grid>
                    }
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default TrainingDetails;